import { z } from 'zod';

import { Role } from '@optical/rbac';

import { isoDatetimeToDate } from '../codecs';

/** Invitation Params */

export type InvitationParams = z.output<typeof invitationParamsSchema>;
export type InvitationParamsDTO = z.input<typeof invitationParamsSchema>;

export type PlayerInvitationParams = z.output<typeof playerInvitationParamsSchema>;

/** A player invitation that links the invitee to an existing roster player. */
export type PlayerInvitationParamsByPlayerId = PlayerInvitationParams & { playerId: string };

/** A player invitation that carries the invitee's name so they can create their own profile. */
export type PlayerInvitationParamsByName = PlayerInvitationParams & {
  firstName: string;
  lastName: string;
};

/**
 * Params for a player user.
 *
 * One branch, not two, and deliberately lenient. Stored invitations predate the split between
 * "link an existing player" and "invite by name", so persisted params may carry a playerId and
 * names together, or neither. A union of two strict branches silently picks the first that
 * matches and strips the rest — dropping the playerId of every invitation written before that
 * split. Which kind an invitation is, is therefore decided from the *values*, by
 * `InvitationUtils.hasPlayerId` / `hasInviteeNames`, and never from the shape.
 *
 * New invitations are held to the stricter rule in `createInvitationSchema`.
 */
export const playerInvitationParamsSchema = z.object({
  email: z.email(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  playerId: z.uuid().optional(),
  role: z.literal(Role.PLAYER),
});

export const invitationParamsSchema = z.discriminatedUnion('role', [
  /** params for customer user */
  z.object({
    email: z.email(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    role: z.literal(Role.CUSTOMER),
  }),
  /** params for guest user */
  z.object({
    email: z.email(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    role: z.literal(Role.GUEST),
  }),
  /** params for player user */
  playerInvitationParamsSchema,
]);

/** Invitation */

export type InvitationId = Invitation['id'];

export type InvitationStatus = (typeof InvitationStatus)[keyof typeof InvitationStatus];

/**
 * The lifecycle state an invitation is in.
 *
 * Derived from `acceptedAt` and `expiresAt` rather than stored, so an invitation that expires
 * while it is on screen reads as expired without anyone having written to it.
 */
export const InvitationStatus = {
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  PENDING: 'pending',
} as const;

export type Invitation = z.output<typeof invitationSchema>;
export type InvitationDTO = z.input<typeof invitationSchema>;

export const invitationSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  teamId: z.uuid(),
  createdAt: isoDatetimeToDate,
  updatedAt: isoDatetimeToDate,
  createdBy: z.uuid(),
  expiresAt: isoDatetimeToDate,
  params: invitationParamsSchema,
  token: z.string(),
  acceptedAt: isoDatetimeToDate.optional(),
  acceptedBy: z.string().nullable().optional(),
});

/** Public invitation */

export type PublicInvitation = z.output<typeof publicInvitationSchema>;
export type PublicInvitationDTO = z.input<typeof publicInvitationSchema>;

/**
 * The invitation params a signup screen may see.
 *
 * Its own shape rather than a reuse of {@link invitationParamsSchema}, because that one is the
 * *storage* shape and carries two things this projection must not: `playerId`, an internal roster
 * identifier, and `role`, which tells an unauthenticated reader what the invitation grants. Only
 * the three values the signup form actually renders survive.
 *
 * Deliberately not a discriminated union. A projection has nothing to discriminate on once `role`
 * is gone, and every branch of the stored union narrows to exactly these fields anyway.
 */
export const publicInvitationParamsSchema = z.object({
  email: z.email(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

/**
 * What an invitation looks like to someone who only holds its token, before they have signed in.
 *
 * The token identifies the invitation but authenticates nobody, and signup links get forwarded, so
 * this deliberately carries none of our internal identifiers: `tenantId`, `teamId`, `createdBy`
 * and the invitation's own `id` are absent by omission, `params.playerId` by the narrower
 * {@link publicInvitationParamsSchema}, and `acceptedBy` — a user id — is gone outright.
 *
 * `acceptedAt` stays. The signup page has to tell a spent link from a live one, and the timestamp
 * carries that without naming anybody; keeping it as a date rather than a boolean is also what
 * lets {@link InvitationUtils.isAccepted} and {@link InvitationUtils.getStatus} read this
 * projection and a full invitation through the same `Pick`.
 */
export const publicInvitationSchema = z.object({
  expiresAt: isoDatetimeToDate,
  params: publicInvitationParamsSchema,
  token: z.string(),
  acceptedAt: isoDatetimeToDate.optional(),
});

export const InvitationUtils = {
  /** Accepts either a full or a public invitation — both carry the fields these read. */
  isAccepted(invitation: Pick<Invitation, 'acceptedAt'>): boolean {
    return !!invitation.acceptedAt;
  },
  isExpired(invitation: Pick<Invitation, 'expiresAt'>): boolean {
    return invitation.expiresAt.getTime() < Date.now();
  },
  /**
   * The lifecycle state of an invitation.
   *
   * Acceptance wins over expiry: an invitation that was accepted before it ran out stays
   * accepted, however long ago that was.
   */
  getStatus(invitation: Pick<Invitation, 'acceptedAt' | 'expiresAt'>): InvitationStatus {
    if (InvitationUtils.isAccepted(invitation)) {
      return InvitationStatus.ACCEPTED;
    }

    if (InvitationUtils.isExpired(invitation)) {
      return InvitationStatus.EXPIRED;
    }

    return InvitationStatus.PENDING;
  },
  /**
   * Whether the invitation links the invitee to an existing roster player.
   *
   * Tests the value, not key presence: params come back from JSON where an absent key and an
   * empty one are not reliably distinguishable, and a key-presence check would also count a
   * `playerId: undefined` left behind by an older writer.
   *
   * Takes precedence over {@link hasInviteeNames} — an invitation that carries both is a link.
   */
  hasPlayerId(params: InvitationParams): params is PlayerInvitationParamsByPlayerId {
    return params.role === Role.PLAYER && Boolean(params.playerId);
  },
  /**
   * Whether the invitation carries the invitee's name so they can be given a new roster player.
   *
   * The counterpart to {@link hasPlayerId}, and the same value-not-key test for the same reason.
   * Both names are required: half a name cannot create a player, and an invitation carrying only
   * a first name is an older writer's leftover rather than an invite-by-name.
   *
   * Yields to {@link hasPlayerId}. An invitation that carries both is a link to an existing
   * player, and the names on it are only there to prefill the signup form — so ask `hasPlayerId`
   * first and treat this as the fallback, never the other way round.
   */
  hasInviteeNames(params: InvitationParams): params is PlayerInvitationParamsByName {
    return params.role === Role.PLAYER && Boolean(params.firstName) && Boolean(params.lastName);
  },
  /**
   * The most recently created invitation that nobody has accepted, or `null` if there is none.
   *
   * This is the one that describes where a player stands: an accepted invitation says nothing
   * about an account that does not exist — if it had worked, the player would be linked — so
   * picking it would show "invited" for someone whose link was already spent.
   *
   * Newest wins among the rest, and that is enough to settle pending against expired without
   * ranking them: an invitation expires a fixed span after it is created, so nothing older than
   * a live invitation can still be live. Callers must not rely on the order they pass in; the
   * store hands these out in whatever order they arrived.
   *
   * @param invitations - The invitations naming one player, in any order.
   */
  getLatestUnaccepted<T extends Pick<Invitation, 'acceptedAt' | 'createdAt'>>(
    invitations: readonly T[],
  ): T | null {
    return invitations.reduce<T | null>((latest, invitation) => {
      if (InvitationUtils.isAccepted(invitation)) {
        return latest;
      }

      if (!latest || invitation.createdAt.getTime() > latest.createdAt.getTime()) {
        return invitation;
      }

      return latest;
    }, null);
  },
};
